import { SingleUseLinkType } from './bookingLinks'
import { Immutable } from './immutability'
import { Products as LicenseProducts, LicensedProduct, SessionLicense } from './license'
import { PickListValue } from './response/crm'

export * from './data-fields'
export * from './fire-workspaces'

export interface ExpiredDays {
  days?: number
}

export type EntityType =
  | 'router'
  | 'user'
  | 'adminSchedulingLink'
  | 'queue'
  | 'groupSchedulingLink'
  | 'ownershipSchedulingLink'
  | 'form.block.mapping'

export interface SlugMappingResponse {
  entityFireId?: string | null
  entityLegacyId?: string | null
  entityType: string
  fireSlug?: string
  legacySlug: string
}

export type MeetingsProductType = 'Hot' | 'Mild' | 'Free' | 'Distribution'

export type IniviteMeetingsProductType = Exclude<MeetingsProductType, 'Chat'> | 'Concierge'

export type WorkspaceType = 'CRM' | 'Events' | 'Scheduling' | 'Recruitment'

export type OnboardingSteps =
  // updated by BE
  | 'CreatedAccountAndUser'
  | 'UpdatedAccountInfo'
  | 'CreatedWorkspace'
  | 'CreatedMeetingType'
  | 'InvitedUsers'
  // updated manually
  | 'ChosenCRM'
  | 'CreatedBookingLinks'

export type Workspaces = {
  adminOf?: string[]
  assignedTo?: string[]
  default?: string
  selected?: string
  selectedType?: WorkspaceType
}

export type AccountOnboardingSessionStep = 'Installed' | 'Uninstalled' | 'Onboarding' | 'Suspended'

export type UserOnboardingSessionStep = 'Installed' | 'Onboarding' | 'OrgOnboarding'

export enum SessionActions {
  EmailSignatureTourCompleted = 'EmailSignatureTourCompleted',
}

export type MeetingSource =
  | 'Online'
  | 'Handoff'
  | 'Booker'
  | 'Phone'
  | 'Video'
  | 'Checkin'
  | 'MeetingRequest'
  | 'ChiliCalendar'

export interface Session {
  accountInstallationStatus?: AccountOnboardingSessionStep
  actionsCompleted?: SessionActions[]
  admin?: boolean
  business?: boolean
  company?: string
  companyDomain?: string
  companyName?: string
  crm?: {
    id: string
    instanceUrl: string
    status: 'Connected' | 'Disconnected' | 'InTrouble'
    type: CrmType
  }
  domain?: string
  domainVerified?: boolean
  email?: string
  id?: string
  isGoogleConnected?: boolean
  isInvited?: boolean
  isMicrosoftConnected?: boolean
  isPlatformConnected?: boolean
  isSalesforceConnected?: boolean
  licences: SessionLicense
  meetingsOnboardingStepsDone?: OnboardingSteps[]
  platformEmail?: string
  products: ProductTypes[]
  subDomain?: string
  tenantId?: string
  token?: string
  user: {
    displayName?: string
    firstName?: string
    id?: string
    lastName?: string
    linkName?: string
    platformFirstName?: string
    platformLastName?: string
  }
  userInstallationStatus?: UserOnboardingSessionStep
  workspaceAdmin?: boolean
  workspaces: Workspaces
}

export interface Workspace {
  active: boolean
  color: string
  default: boolean
  deleted: boolean
  emoji?: string
  event?: Event
  eventId?: string
  id: string
  integrations: ['Salesforce'?, 'Convey'?, 'Hubspot'?]
  isDistribution?: boolean
  link: string
  logo?: string
  name: string
  order: number
  productTypes: {
    meetingsType: MeetingsProductType // Don't use it as a raw value, use func `workspaceFeatureType`
  }
  selected: boolean
  type?: WorkspaceType
  usersCount: number
  workspaceType: WorkspaceType
}

export interface WorkspaceLicenseUsage {
  free: number
  hot: number
  mild: number
}

export interface RecentWorkspace extends Omit<Workspace, 'usersCount' | 'workspaceType'> {
  type: WorkspaceType
  users: string[]
  usersNum: number
}

export enum WorkspaceRoles {
  Admin = 'Admin',
  User = 'User',
}

export enum InviteToMeetingsTarget {
  Email = 'Email',
  Salesforce = 'Salesforce',
}

export interface InviteToMeetings {
  id: string
  inviter: {
    email: string
    name: string
    userId: string
  }
  lastSent: number
  settings: {
    licences: LicensedProduct[]
    workspaces: { id: string; role: WorkspaceRoles }[]
  }
  status: string
  target: {
    email?: string
    name?: string
    tpe: InviteToMeetingsTarget
    value: string
  }
}

export type DraftInviteToMeetings = Omit<InviteToMeetings, 'id' | 'lastSent' | 'status' | 'inviter'>

export interface BookerPreferences {
  allowBusy: boolean
  allowReassign: boolean
  allowReserve: boolean
  deleteInCrm: boolean
  endlessRoundRobin: boolean
  excludeCancelled: boolean
  excludeNoShow: boolean
  hideReport: boolean
  id: string
  matchContact: boolean
  matchContactWebsite: boolean
  roundRobinStart: number
  showAllTitles: boolean
  showNext: boolean
  skipVacations: boolean
  span: number
}

export interface BookerEvent {
  assigneeId: string
  assigneeIds: string[]
  attended: boolean
  attendees: Attendee[]
  creationDate?: string
  dates: AvailableHours[]
  deleted: boolean
  description?: string
  draft?: object
  end: number
  goal: number
  id?: string
  image?: string
  location?: string
  members: Member[]
  name: string
  roomsIds: string[]
  start: number
  status?: string
  timezone: string
  workspaceId: string
}

export interface IdNameEmail {
  email: string
  id: string
  name: string
}

export interface WorkspaceUser extends IdNameEmail {
  googleStatus?: string
  hasExtension?: boolean
  microsoftStatus?: string
}

export interface Invitee {
  auto?: boolean
  email: string
  id?: string
  mandatory?: boolean
  name?: string
  shadow?: boolean
}

export interface BookSelection {
  end: number
  forSelection: boolean
  // I am not sure why id is needed. Currently we send new Date().valueOf()
  id: number
  slotId: number
  start: number
}

export interface BookRequest {
  assigneeEmail?: string
  assigneeId: string
  assigneesInfo: QueueMember[]
  bookerInfo: BookerInfo
  description: string
  duration: number
  endDate: number
  id: string | null
  inviteProspect: boolean
  invitees: Invitee[]
  isLead: boolean
  locale: string
  location: string
  mandatoryGuests?: string[]
  multipleSelection: BookSelection[]
  notifyGuests?: boolean
  orderPositions?: { firstInQueueId: string }
  prospect: string
  prospectName?: string
  prospectPhone?: string
  queueId?: string
  rawDetails?: {
    description: string
    location: string
    title: string
  }
  reportId?: string
  roomId?: string | null
  sequentials: []
  source: MeetingSource
  startDate: number
  template: string
  timezone: string
  title: string
  workspaceId?: string
}

export interface BookerRules {
  id: string
  owners: string[]
  success: boolean
}

export interface BookerInitiateCall {
  success: boolean
}

export interface PhoneParseString {
  text: string
}

export interface PhoneParseMessageParams {
  routeId: string
  text: string
}

export interface Meeting {
  description: string
  endDate: number
  invitees: Invitee[]
  location: string
  pickedMeetingLocation?: PickedMeetingLocation
  prospect?: string
  prospectName?: string
  sequentials: Array<{ end: number }>
  startDate: number
  timezone: string
  title: string
}

export interface ReportEvent extends BookerCalendarEvent {
  description: string
  title: string
}

export interface Report {
  assigned: boolean
  assignee?: IdNameEmail
  bookRequest: BookRequest
  booker: IdNameEmail
  bookingLinkId?: string
  buffers: string[]
  creationDate: string
  crmId?: string
  event: BookerEvent
  eventId: string
  eventLink: string
  firstInQueue: IdNameEmail
  id: string
  isCancelledByProspect?: boolean
  marketingId: string
  meetingSeries?: {
    id: string
    prospects: Prospect[]
  }
  nextInQueue: IdNameEmail
  parseValues?: {
    key: string
    value: string
  }[]
  prospectInfo?: Prospect
  queue?: Queue
  rescheduled: number
  salesforceId: string
  status?: string
  template: string
  utmParameters?: Record<string, string>
  workspaceId: string
}

export interface BookerInfoResponse {
  accessible: boolean
  id: string
  info: {
    activationDate: number
    calibration: number
    eventsSize: number
    rate: number
    weight: number
  }
  meetingCapReached: boolean
  onVacation: boolean
  order: number
  timezone: string
  workingHoursSet: boolean
}

export interface BookerCalendarEvent {
  assigneeId?: string
  assigneeIds: string[]
  attended: boolean
  attendees: Attendee[]
  calendarSource?: 'Report' | 'Outside'
  cappedByTemplateAssigneeIds: string[]
  end: number
  fromBooker: boolean
  googleEventId?: string
  id: string
  isBusy: boolean
  isFullDay: boolean
  isGuest: boolean
  isPrivate: boolean
  queueId?: string
  recurrent: boolean
  recurringEventId?: string
  roomsIds: string[]
  sequentials: BookerCalendar[]
  start: number
  title?: string
  updated?: string
}

export interface BookerCalendar {
  events: BookerCalendarEvent[]
}

export interface BookerRescheduleReassign {
  addToCalendarLocation?: string
  assigneeCrmId?: string
  assigneeId?: string
  assigneeSFId?: string
  bookMeeting: BookMeeting
  error?: 'ProspectMaxMeetingsReached' | 'DomainMaxMeetingsReached'
  googleId?: string
  hasAccess: boolean
  htmlLink?: string
  reportId?: string
  salesforceId?: string
  secret?: string
}

export interface BookerVideoCallStatus {
  assigneeId: string
  meetingUrl?: string
  status?: string
  videoCallId: string
}

export interface CurrentBook {
  bookMeeting: BookMeeting
  bookResult?: BookerRescheduleReassign
  bookedDate?: string
  booker: string
  created: string
  id: string
  inProgress: boolean
  isBooked: boolean
  isCanceled?: boolean
  isRescheduled: boolean
  marketingId?: string
  prospect?: string
  router?: string
  selectedSlotId?: number
}

export interface BookMe {
  bookWithMe: CurrentBook
  memberLink: string
  token: string
}

export interface BookMeRouter {
  isProspectInSalesforce: boolean
  link?: string
}

export interface BookWithMe {
  bookWithMe: BookMe
  gid: string
  token: string
}

export interface BookerReassign {
  booker: Invitee
  end: number
  errorCode: number
  guests: Invitee[]
  prospect: Prospect
  queueId: string
  start: number
  templateId: string
  workspaceId: string
}

export interface BookerRelatedView {
  contactId?: string
  date?: number
  id: string
  name?: string
}

export interface BookerRelatedTo {
  cases: BookerRelatedView[]
  deals: BookerRelatedView[]
  opportunities: BookerRelatedView[]
  tickets: BookerRelatedView[]
}

export type ProductStatus = 'Active' | 'Inactive'

export interface UserAccess {
  active: boolean
  admin: boolean
  defaultWorkspaceId?: string
  email: string
  gotoMeetingValid: null
  hasExtension: boolean
  hasGoogle: boolean
  hasGotoMeeting: boolean
  hasMicrosoft: boolean
  hasRingCentral: boolean
  hasWebEx: boolean
  hasZoom: boolean
  id: string
  lastLoggedIn: string
  lastMeetBooked: number
  lastMeetReceived: number
  licences: {
    [key in LicenseProducts]: {
      status: ProductStatus
    }
  }
  name: string
  queues: Array<{
    id: string
    isDefaultBooker: boolean
    name: string
    workspaceId: string
  }>
  workspaceAdmin: boolean
  workspaces: string[]
}

export interface UsersAtAllResponse {
  list: UserAccess[]
  total: number
}

export interface ParticipantByEmailResponse {
  contacts: Prospect[]
  instanceUrl: string
  leads: Prospect[]
  prospect: Prospect
  total: number
  whichName: string
}

export interface PrintableBrief {
  account?: string
  accountExecutive?: string
  accountOwner?: string
  companyName?: string
  contractedProductsRollup?: string
  date?: string
  description?: string
  internalAttendees: Attendee[]
  location?: string
  marketSegment?: string
  opportunities: Opportunity[]
  prospectAttendees: Attendee[]
  time?: string
  title?: string
}

export type AttendeeCheckedIn = 'CheckedIn' | 'NoShow' | 'Maybe'

export type ResponseStatus = 'Accepted' | 'Declined' | 'Tentative' | 'Pending'

export interface Attendee {
  checkedIn: AttendeeCheckedIn
  crmId?: string
  email: string
  isMain: boolean
  name?: string
  responseStatus?: ResponseStatus
}

export interface Opportunity {
  accountId: string
  closeDate?: string
  id: string
  name?: string
  owner?: string
  products?: string
  stage?: string
  type?: string
}

export interface GuestSession {
  guest: string
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  options: {}
  success?: boolean
  token: string
}

export interface BookMeSession {
  bookMe: {
    bookMeeting: BookMeeting
    isCanceled: boolean
  }
  code: number
  reason: string
  sessionId: string
  success: boolean
  tenantId: string
}

export type ProductTypes =
  | 'Meetings'
  | 'MildMeetings'
  | 'Inbox'
  | 'Events'
  | 'Calendar'
  | 'Recruiting'
  | 'Enterprise'
  | 'Concierge'
  | 'Distribution'

export interface Product {
  enabled: boolean
  name: ProductTypes
}

export interface WorkingTime {
  hours: number
  minutes: number
}

export interface WorkingInterval {
  workEnds: WorkingTime
  workStarts: WorkingTime
}

export interface Widget {
  formId?: string
  id?: string
  userId?: string
  uuid?: string
  view: View
}

export interface View {
  available: boolean
  button: Button
  connections: Connection[]
  greeting?: string
  offlineMessage?: string
  photo?: string
  showPhoto: boolean
  texts: { [key: string]: any }
}

export interface VideoText {
  text: string
}

export interface ValidateQueues {
  queues: ValidateQueue[]
}

export interface ValidateQueue {
  queueId: string
  validationType: any
}

export interface UserView {
  admin: boolean
  email: string
  id?: string
  name: string
}

export interface UsersRequest {
  activeSalesforce: boolean
  exclude?: string[]
  include?: string[]
  ordering: string[]
  page: number
  query?: string
  size: number
  workspaceId?: string
}

export interface Texts {
  call?: CallText
  chat?: ChatText
  email?: EmailText
  main?: MainText
  schedule?: ScheduleText
  video?: VideoText
}

export interface TemplateDescription {
  id: string
  order: number
}

export interface TemplateAssigneeName {
  name?: string
}

export interface Slot {
  end: string
  forSelection: boolean
  groupBy?: string
  id?: string
  sequentials: Sequential[]
  slotId?: string
  start: string
}

export interface Settings {
  color?: string
  selected: boolean
}

export interface Sequential {
  assigneeId?: string
  end: number
  info: Info
  start: number
}

export interface ScheduleText {
  text: string
}

export interface Salesforce {
  createOnBooking: boolean
  createRealtime: boolean
  createStrategy: any
  createTask: boolean
  createdBy?: string
  updateStrategy: any
}

export type CrmOperator =
  | '='
  | '!='
  | '<'
  | '>'
  | '<='
  | '>='
  | 'contains'
  | 'not contains'
  | 'startsWith'
  | 'startsWith(csv)'
  | '!= null'
  | '= null'
  | 'equal'
  | 'notEqual'
  | 'containsAnyOf'
  | 'containsNoneOf'

export type CrmEntity =
  | 'Contact'
  | 'CaseOwner'
  | 'Opportunity'
  | 'OpportunityOwner'
  | 'LeadOwner'
  | 'Lead'
  | 'ContactOwner'
  | 'Account'
  | 'Event'
  | 'Case'
  | 'AccountOwner'
  | 'User'

export interface Rule {
  entity: string
  field: string
  id?: string
  operator: CrmOperator
  tag?: string
  value?: string
}

export interface RoomOrder {
  order: number
  roomId?: string
}

export type RoomAlgorithm = 'AdvancedRoundRobin' | 'PriorityBased'

export interface RoomListDescription {
  algorithm: RoomAlgorithm
  list: RoomOrder[]
}

export interface Resource {
  assigneesIds: string[]
  groupId?: string
  selectOption?: any
  state?: any
}

export interface RequestFlow {
  active: boolean
  createdBy?: string
  eventId?: string
  form?: Form
  groups: Group[]
  id?: string
  linkName?: string
  name: string
  proposeTime: boolean
  roomsIds: string[]
  salesforce?: Salesforce
  shouldCreateProspect: boolean
  shouldCreateUpdateProspect: boolean
  shouldUpdateProspect: boolean
  templates: string[]
  type: any
}

export interface RawDetails {
  description?: string
  location?: string
  title?: string
}

type QueueOwnership = 'Assignee' | 'AssigneeWithoutBooker' | 'Booker'

export type QueueAlgorithm =
  | 'Group'
  | 'Ownership'
  | 'Manual'
  | 'PlaceHolder'
  | 'RoundRobin'
  | 'Sequential'
  | 'IndividualLinks'

export interface Queue {
  active: boolean
  addCrm: boolean
  algorithm: QueueAlgorithm
  bookerMandatory?: boolean
  creationDateTime?: number
  enableBroadReschedule?: boolean
  eventId?: string
  eventsInSlot?: number
  formula?: string
  handoff?: boolean
  hotHandoff: boolean
  id: string
  members: Member[]
  membersIds?: string[]
  name: string
  onlineSettings?: OnlineSettings
  ownership: QueueOwnership
  phoneActionNoReps?: any
  phoneActionProspectQualified?: any
  rooms?: RoomListDescription
  rules: Rule[]
  strictOwnership?: boolean
  template: string
  withBookerGuest: boolean
  workspaceId?: string
}

export interface Prospect {
  accountId?: string
  accountUrl?: string
  address?: Address
  companyName?: string
  converted: boolean
  convertedAccountId?: string
  convertedContactId?: string
  createdDate?: number
  email: string
  emea?: object
  employees?: string
  fullName?: string
  id: string
  inCrm: boolean
  inSalesforce: boolean
  isLead: boolean
  link?: string
  noShow?: object
  notes?: string
  owner?: string
  ownerEmail?: string
  ownerName?: string
  phone?: string
  prospectType: string
  prospectUrl?: string
  responseStatus?: string
  source: 'User' | 'Salesforce' | 'Google' | 'Microsoft' | 'Manual' | 'Hubspot'
  title?: string
  url?: string
}

export interface Case {
  caseNumber: string
  contact: Prospect
  contactId: string
  description: string
  id: string
  subject: string
}

export interface ParseTemplates {
  list: ParseTemplate[]
}

export interface ParseTemplate {
  assigneeId?: string
  caseId?: string
  clearEmpty?: object
  conveyRequestId?: string
  eventId?: string
  opportunityId?: string
  prospectId?: string
  queueId?: string
  rawProspect?: Prospect
  reportId?: string
  roomId?: string
  template?: string
  title?: string
  workspaceId?: string
}

export interface Pager {
  exclude?: string[]
  include?: string[]
  page: number
  query: string
  size: number
  workspaceId?: string
}

export interface PagedRequest {
  page: number
  query?: string
  size: number
}

export interface OrderPositions {
  firstInQueueId?: string
}

export interface OnlineSettings {
  defaultBooker?: string
  linkName?: string
  online: boolean
  onlineSelector?: OnlineSelector
  placeholderId?: string
}

export interface OnlineSelector {
  enabled: boolean
  templates: TemplateDescription[]
  welcome?: string
}

export interface MemberInfo {
  accessible: boolean
  available: boolean
  id?: string
  info?: Info
  meetingCapReached: boolean
  onVacation: boolean
  order: number
  timezone?: string
}

export interface Member {
  activationDate?: number
  active: boolean
  calibration?: object
  email: string
  groupMember?: boolean
  id: string
  initialOrder: number
  lock: boolean
  main?: boolean
  mandatory?: boolean
  meetingCapReached?: boolean
  name: string
  order: number
  timezone?: string
  weight: number
  weightRate?: number
}

export interface QueueMember
  extends Member,
    Pick<MemberInfo, 'onVacation' | 'order' | 'accessible'> {
  available?: boolean
}

export interface MeetingRequest {
  account?: Account
  approvedBy: string[]
  approvers: Approver[]
  creationDate: number
  declinedBy: string[]
  description?: string
  end: number
  eventId?: string
  flowId?: string
  groups: AssigneeGroup[]
  guests: Guest[]
  history: HistoryItem[]
  id?: string
  isBooked: boolean
  location?: string
  prospect?: Prospect
  requesterId?: string
  resources: Resource[]
  roomId?: string
  start: number
  templateId?: string
  title?: string
  values: { [key: string]: string[] }
}

export interface MainText {
  offline: string
  welcome: string
}

export interface Locales {
  error?: string
  label?: string
}

export interface Info {
  activationDate?: object
  calibration: number
  eventsSize: number
  rate: number
  uiOrder: number
  weight: number
}

export interface HistoryItem {
  author: Author
  createdAt: number
  itemType: any
}

export interface ReportHistoryItem {
  action: 'CrmEventCreation' | 'MeetingReassigned'
  change?: {
    by?: string
    from: string
    to: string
  }
  status: 'Success' | 'Failure'
  text: string
  time: number
}

export interface GuestsRequests {
  accountId?: string
  emails: string[]
}

export interface Guest {
  checkedIn: any
  email?: string
}

export interface Group {
  assigneeGroup?: string
  assigneesIds: string[]
  mandatory: boolean
  name?: string
  selectOption: any
}

export interface GlobalSettings {
  allowBookingIntoNonMatchingQueues?: boolean
  allowBusy: boolean
  allowReassign: boolean
  allowReserve: boolean
  crmEvent?: WorkspaceCrmEventSettings
  deleteInCrm: boolean
  deleteInSalesforce?: boolean
  enabledLanguages: string[]
  endlessRoundRobin: boolean
  excludeCancelled: boolean
  excludeNoShow: boolean
  hideReport: boolean
  id: string
  matchContact: boolean
  matchContactWebsite: boolean
  multiStrategy?: any
  roundRobinStart: number
  showAllTitles: boolean
  showNext: boolean
  skipVacations: boolean
  span: number
  spanMillis: number
}

export type WorkspaceCrmEventSettings = {
  delete: DeleteEventStrategy
  isPerWorkspace: boolean
  settings: {
    campaignId?: string
    create: CreateEventStrategy
    relateTo: ObjectRelate
  }
}

export interface Form {
  blocks: Block[]
  buttonLabel?: string
  default: boolean
  description?: string
  headline?: string
  id?: string
  linkName?: string
  name: string
  userId?: string
  workspaceId?: string
}

export interface FeaturePost {
  creators: string[]
  description: string
  name: string
  url: string
}

export interface EventsRequest {
  calendarId: string
  from: number
  nextPage?: string
  to: number
}

export interface EventPager {
  eventId?: string
  page: number
  query?: string
  size: number
}

export interface Event {
  creationDate?: string
  dates: AvailableHours[]
  deleted: boolean
  draft?: object
  end?: number
  goal: number
  id?: string
  image?: string
  location?: string
  members: Member[]
  name?: string
  start?: number
  timezone?: string
  workspaceId?: string
}

export interface EmailText {
  text: string
}

export interface Day {
  day: number
  month: number
  year: number
}

export interface DateTimeZone {
  fixed: boolean
  id: string
}

export interface CreateUpdateRequest {
  calendarId: string
  description?: string
  eventId?: string
  from: number
  guests: Guest[]
  location?: string
  templateId?: string
  timezone?: string
  title?: string
  to: number
}

export interface Connection {
  href?: string
  name: any
}

export interface Chronology {
  zone: DateTimeZone
}

export interface Chiller {
  email: string
  id?: string
  name: string
}

export interface ChatText {
  text: string
}

export interface CallText {
  text: string
}

export interface CalendarsRequest {
  nextPage?: string
}

export interface Button {
  color: string
  corner: number
  font: string
  hoverLabel: string
  label: string
}

export interface GetInfoDataType {
  assigneesIds: string[]
  end: number
  guests: string[]
  queueId?: string
  roomsIds?: string[]
  routeId?: string
  slot?: number
  start: number
  step: number
  template: string
  timezone: string
  withBuffers: true
}

export interface BookMeeting {
  assigneeId?: string
  assigneesInfo: MemberInfo[]
  bookerInfo?: BookerInfo
  bookingId?: string
  bookingUrl?: string
  calendarId?: string
  description: string
  duration?: number
  endDate: number
  guests: string[]
  id?: string
  inviteProspect: boolean
  invitees: Invitee[]
  isLead: boolean
  location: string
  mandatoryGuests: string[]
  marketingId?: string
  meetingSeriesId?: string
  multipleSelection: Slot[]
  notifyGuests: boolean
  online: boolean
  orderPositions?: OrderPositions
  organizationDomain?: string
  pickedMeetingLocation?: PickedMeetingLocation
  possibleMeetingLocations?: LocationType[]
  prospect?: string
  prospectName?: string
  prospectPhone?: string
  prospectValues?: object
  queueId?: string
  rawDetails?: RawDetails
  relateTo?: string
  reportId?: string
  requestId?: string
  rescheduleId?: string
  roomId?: string
  routeId?: string
  selectedSlot?: string
  sequentials: Sequential[]
  singleUseLinkId?: string
  source?: any
  startDate: number
  template?: string
  timezone: string
  title: string
  userId?: string
  workspaceId?: string
}

export interface BookerInfo {
  assigneesGroupIds: string[]
  assigneesIds: string[]
  end: number
  eventId?: string
  guests?: string[]
  meetingDuration?: number
  queueId?: string
  roomsIds: string[]
  slot?: number
  source?: any
  start: number
  step: number
  template?: string
  withAvailableDays?: boolean
  withBuffers?: boolean
  withDelay?: boolean
}

export interface Block {
  contactHSField?: string
  contactSFField?: string
  default?: boolean
  defaultValue?: string
  description?: string
  error?: string
  fieldDescription?: { [key: string]: object }
  fieldName: string
  fieldType?: string
  hidden?: boolean
  index?: number
  label?: string
  leadSFField?: string
  locales?: { [key: string]: any }
  options: string[]
  pickListId?: string
  required: boolean
  restricted: boolean
  section?: any
  supportedLocales?: string[]
  tag?: string
  updateField?: boolean
}

export interface AvailableHours {
  active: boolean
  date: Day
  day: string
  workingIntervals: WorkingInterval[]
}

export type HourPerDay = Omit<AvailableHours, 'date'> & {
  date?: AvailableHours['date']
}

export type HourPerDayRecord<T extends string | number | symbol = string> = Record<T, HourPerDay>

export type AvailableHoursRecord = Record<
  string,
  AvailableHours & {
    weekDay: string
  }
>

export enum WorkingHoursSource {
  Event = 'Event',
  Template = 'Template',
}

export interface Availability {
  advancePeriod?: 'Day' | 'Week' | 'Month'
  advanceValue?: number
  custom?: boolean
  customEnd?: number
  customHoursPerDay?: AvailableHours[]
  customStart?: number
  dateMeetingEnd?: string
  dateMeetingStart?: string
  dates?: AvailableHours[]
  dynamicSlot?: boolean
  hoursPerDay?: HourPerDayRecord
  online?: boolean
  respectAssigneeHours?: boolean
  respectAssigneeTimezone?: boolean
  scheduleNotice?: {
    period?: 'Minute' | 'Hour' | 'Day' | 'Week' | 'Month'
    value?: number
  }
  showTimezoneToProspect?: boolean
  slotSize?: number
  specificDate?: boolean
  timeZone?: string
  workingHoursSource?: WorkingHoursSource
}

export interface Author {
  id?: string
  name?: string
}

export type AssigneeGroup = Immutable<{
  assignees: string[]
  created: string
  eventAssignees: boolean
  eventId?: string
  groupManager?: string
  id: string
  name: string
}>

export interface AssignBody {
  assigneeId?: string
  groupId?: string
  requestId?: string
}

export interface Approver {
  approverId: object
  id: object
}

export interface Address {
  city?: string
  country?: string
  postalCode?: string
  state?: string
  street?: string
}

export interface Account {
  id?: string
  name: string
}

export type BusinessCalendarsConnection = Record<string, boolean>

export interface Translations {
  concierge: Record<string, string>
}

export interface Placeholder {
  calendarId: string
  capacity: number
  eventId: string
  id: string
  isActive: boolean
  name: string
  owner: Owner
}

export interface Owner {
  email: string
  id: string
  name: string
}

export interface PlaceholderDAO {
  capacity: number
  eventId: string
  id?: string
  name: string
  userId: string
}

export interface PagedResponse<T> {
  page: number
  result: Array<T>
  size: number
  total: number
}

export interface UserSearch {
  email: string
  id: string
  name: string
}

export interface TemplateAvailability {
  advancePeriod?: 'Week' | 'Day' | 'Month'
  advanceValue?: number
  custom?: boolean
  customHoursPerDay?: AvailableHours[]
  dateMeetingEnd?: string
  dateMeetingStart?: string
  dates?: AvailableHours[]
  dynamicSlot?: boolean
  hoursPerDay?: HourPerDayRecord
  online?: boolean
  respectAssigneeHours?: boolean
  respectAssigneeTimezone?: boolean
  scheduleNotice?: {
    period?: 'Minute' | 'Hour' | 'Day' | 'Week' | 'Month'
    value?: number
  }
  showTimezoneToProspect?: boolean
  slotSize?: number
  specificDate?: boolean
  timeZone?: string
  workingHoursSource?: WorkingHoursSource
}

export interface UTMParameters {
  campaign: string
  content?: string
  medium: string
  source: string
  term?: string
}

export enum UtmConfiguration {
  UtmConfigurationDisabled = 'UtmConfigurationDisabled',
  UtmConfigurationEnabled = 'UtmConfigurationEnabled',
}

export interface Template {
  active: boolean
  // if present, it is own (personal) template
  assigneeMeetingsCap?: {
    numberOfMeetings: number
    timeUnit: 'Day' | 'Week' | 'Month'
  }
  availability: TemplateAvailability
  buffers?: {
    after: number
    before: number
  }
  createBuffersInIB: boolean
  createEventInCrm?: boolean
  createEventInSalesforce?: boolean
  createPlatformConference: boolean
  createdAt?: string
  default: boolean
  description: string
  duration: number
  editable: boolean
  eventId?: string
  expireLink: boolean
  form?: string
  guestsCanModifyEvent: boolean
  id: string
  linkName: string
  location: string
  makeAssigneeOwner: boolean
  maxMeetings?: {
    filterBy: 'Email' | 'Domain'
    numberOfMeetings: number
    timeFrame: number
    timeUnit: 'Hour' | 'Day' | 'Week' | 'Month'
  }
  meetingApprovalFlow?: boolean
  name: string
  oneClickBookingRequiresFormAnyway?: boolean
  onlineDescription?: string
  possibleMeetingLocations?: LocationType[]
  relateTo?: {
    enabled: boolean
    to: ObjectRelate
  }
  singleUseLink?: boolean
  title: string
  transparent: boolean
  unSelected?: boolean
  updatedAt?: string
  userId?: string
  utmConfiguration?: {
    type?: UtmConfiguration
    value?: UTMParameters
  }
  withoutProspect: boolean
  workspaceId: string
}

export type Tag = {
  name: string
  objectType: string
  sourceTag: string
}

export type RawDynamicTagLocation = {
  isDefault: boolean
  type: 'RawDynamicTag'
} & Tag

export type CustomTextFieldLocation = {
  fieldType: 'Custom' | 'GoToMeeting' | 'Webex' | 'RingCentralVideo' | 'Zoom' | 'Hopin' | 'Gong'
  isDefault: boolean
  text: string
  type: 'CustomTextField'
}

export type GoogleMeetLocation = {
  isDefault: boolean
  type: 'GoogleMeet'
}

export type MicrosoftTeamsLocation = {
  isDefault: boolean
  type: 'MicrosoftTeams'
}

export type AskGuestLocation = {
  isDefault: boolean
  type: 'AskTheGuest'
}

export type PickedAskGuestLocation = Pick<AskGuestLocation, 'type'> & { text: string }

export type PickedCallTheGuestLocation = {
  phone: string
  type: 'CallTheGuest'
}

export type CallTheGuestLocation = {
  isDefault: boolean
  type: 'CallTheGuest'
}

export type LocationType =
  | RawDynamicTagLocation
  | CustomTextFieldLocation
  | GoogleMeetLocation
  | MicrosoftTeamsLocation
  | AskGuestLocation
  | CallTheGuestLocation

export type PickedMeetingLocation =
  | PickedAskGuestLocation
  | RawDynamicTagLocation
  | CustomTextFieldLocation
  | GoogleMeetLocation
  | MicrosoftTeamsLocation
  | PickedCallTheGuestLocation

export interface GroupEvent {
  active: boolean
  amountOfGuests?: number
  assignees: string[]
  bookOverBusySlots: boolean
  canGuestsSeeEachOther: boolean
  createCrmEvent?: false
  createSalesforceEvent?: false
  defaultBooker: string
  eventId: string
  id: string
  limitAmountOfGuests: boolean
  mainAssignee: string
  meetings: {
    date: number
    hour: number
    minutes: number
    templateId: string
  }[]
  messageFullMeeting: string
  multipleTemplates: boolean
  name: string
  numberOfMeetings: number
  oneEventPerGuest: boolean
  repeatMeeting: string
  slots: GroupEventSlot[]
  templateId: string
  timezone: string
  welcomeMessage?: string
  workspaceId: string
}

export interface GroupEventSlot {
  day: number
  hour: number
  id?: string
  minute: number
  month: number
  templateId: string
  year: number
}

export interface SeriesStatus {
  active: boolean
  amountOfGuests: number
  currentAmountOfGuests: number
  isGroup: boolean
  limit: boolean
  limitReachedMessage: string
  meetingTimes: {
    end: string
    start: string
  }[]
  name: string
  timezone: string
  welcomeMessage: string
}

export interface RoomAvailability {
  id: string
  isAvailable: boolean
  location?: string
  name: string
  queues: string[]
}

export interface EventUserAvailability {
  displayName: string
  email: string
  meetingTitle?: string
  reportId?: string
  type: 'MeetupUserUnavailable' | 'MeetupUserAvailable'
  userId: string
}

export interface SortedRooms {
  order: number
  roomId: string
}

export interface BusinessWithMicrosoft {
  result: boolean
}

export interface BusinessUserConnections {
  connected: boolean
  connectionUri: string
  displayName: string
  email: string
  healthy: boolean
  hint: string
  system: 'salesforce' | 'google' | 'gotomeeting' | 'webex' | 'zoom' | 'office' | 'ringcentral'
  title: string
}

export type SystemTypes =
  | 'salesforce'
  | 'google'
  | 'gmail'
  | 'gotomeeting'
  | 'webex'
  | 'zoom'
  | 'hubspot'
  | 'office'
  | 'ringcentral'
  | 'hopin'
  | 'gong'

export interface ThirdPartyConnection {
  connected: boolean
  connectionUri: string
  displayName: string
  email: string
  errorMessage?: string
  healthy: boolean
  hint: string
  system: SystemTypes
  title: string
}

export interface CrmStatusResponse {
  connectionType?: 'Hubspot' | 'Salesforce'
  displayName?: string
  email?: string
  exists?: boolean
  sandbox?: boolean
  valid?: boolean
}

export interface HubspotAdminResponse {
  id: string
  name: string
}

export interface TwilioStatusResponse {
  exists: true
  settings?: {
    accountSid: string
    authToken: string
    isSubaccount: boolean
    phoneNumber: string
  }
}

export interface TwilioConnectBody {
  accountSid: string
  authToken: string
}

export interface TwilioPhoneVerifyPayload {
  dialCode: string
  id: string
  phone: string
}

export interface TwilioPhoneVerifyResponse {
  alreadyValid: boolean
  success: boolean
  validationCode: string
}

export interface TwilioPhoneStatusResponse {
  isPhoneVerified: boolean
  validating: boolean
}

export interface TimezoneUserSettingsResponse {
  settings: {
    preferredTimezone: string
    timezone: string
    usePreferredTimezone: boolean
  }
}

export interface TimezoneUserSettingsPayload {
  preferredTimezone: string
  usePreferredTimezone: boolean
}

export interface IntercomStatusResponse {
  exists: true
  settings?: Array<{ workspaceName: string }>
}

export interface SlackStatusResponse {
  exists: boolean
  isValid: boolean
  settings?: Array<{
    admin?: PlatformAdmin
    appId: string
    createdAt?: string
    status: string
    updatedAt?: string
  }>
}

export interface PlatformAdmin {
  displayName: string
  email: string
  id: string
  isAdmin: boolean
}

export type ChromePushNotificationDetail = Immutable<{
  endpoint: string
  keys: {
    auth: string
    key: string
  }
}>

export type TemplateDetails = Immutable<{
  chromePushNotificationDetails: ChromePushNotificationDetail[]
  conference: string
  dialCode: string
  firstName: string
  id: string
  isPhoneVerified: boolean
  isValidatingPhone: boolean
  lastName: string
  location: string
  phone: string
  title: string
}>

export type UserWithSalesforce = Partial<{
  connections: {
    salesforce: {
      active: true
      displayName: string
      id: string
      status: string
      username: string
    }
  }
  displayName: string
  email: string
  id: string
}>

export type UserWithSalesforcePaged = {
  result: UserWithSalesforce[]
} & Partial<PagedRequest>

export type UserWithSalesforceRequest = {
  workspaceId: string
} & Partial<PagedRequest>

export type ConnectionStatus = 'Connected' | 'Disconnected' | 'InTrouble'

export type SalesforceUser = Partial<{
  connectionStatus: ConnectionStatus
  crmId: string
  displayName: string
  email: string
  id: string
  isActive: boolean
}>

export enum ObjectRelate {
  Account = 'Account',
  Campaign = 'Campaign',
  Case = 'Case',
  Company = 'Company',
  Deal = 'Deal',
  Opportunity = 'Opportunity',
  Ticket = 'Ticket',
}

export enum CreateEventStrategy {
  Create = 'Create',
  Disabled = 'Disabled',
}

export enum DeleteEventStrategy {
  Delete = 'Delete',
  Keep = 'Keep',
}

export enum InviteInternalUsersStrategy {
  Exclude = 'Exclude',
  Invite = 'Invite',
}

export enum ScheduledBy {
  Assignee = 'Assignee',
  Booker = 'Booker',
}

export type CrmTemplateEventSettings = {
  id: string
  name: string
  settings: {
    campaignId?: string
    create: CreateEventStrategy
    relateTo: ObjectRelate
  }
}

export type CrmEventWorkspaceSettings = {
  delete: DeleteEventStrategy
  isPerWorkspace: boolean
  scheduledBy: ScheduledBy
  settings: {
    campaignId?: string
    create: CreateEventStrategy
    relateTo: ObjectRelate
  }
  templates: CrmTemplateEventSettings[]
}

export type CrmEventWorkspaceSettingsPayload = {
  delete?: DeleteEventStrategy
  isPerWorkspace?: boolean
  scheduledBy?: ScheduledBy
  settings: {
    create?: CreateEventStrategy
    relateTo?: ObjectRelate
  }
}

export type CrmTemplateEventSettingsPayload = {
  id: string
  settings: {
    create?: CreateEventStrategy
    relateTo?: ObjectRelate
  }
}

export type PersonalCrmEventSettings = {
  isAppliedToAllTemplates: boolean
  settings: {
    create: CreateEventStrategy
    relateTo: ObjectRelate
  }
  templates: CrmTemplateEventSettings[]
}

export type SalesforceQueuesResponse =
  | {
      records: {
        id?: string
        name: string
        queueId: string
        sType: string
      }[]
    }
  | { success: boolean }

export type PersonalTemplateCrmEventSettingsPayload = {
  create: CreateEventStrategy
  id: string
  relateTo: ObjectRelate
}

export type PersonalCrmEventSettingsPayload = Omit<PersonalCrmEventSettings, 'templates'>

export enum MatchWebsiteStrategy {
  Disabled = 'Disabled',
  Match = 'Match',
}

export enum MultiStrategy {
  CreateLead = 'CreateLead',
  MatchBiggest = 'MatchBiggest',
  MatchLatest = 'MatchLatest',
}

export enum UpdateOwnerStrategy {
  Disabled = 'Disabled',
  Update = 'Update',
}

export enum MatchingStrategy {
  CreateContact = 'CreateContact',
  Disabled = 'Disabled',
}

export enum NonMatchingStrategy {
  CreateLead = 'CreateLead',
  Disabled = 'Disabled',
}

export type NonMatchingSettings = { strategy: NonMatchingStrategy }

export type MatchingSettings = {
  matchWebsite: MatchWebsiteStrategy
  multiStrategy: MultiStrategy
  strategy: MatchingStrategy
}

export type CreateMemberSettings = {
  campaignId?: string
  isEnabled: boolean
  memberStatus?: string
}

export type CrmContactSettings = {
  createMember: CreateMemberSettings
  matching: MatchingSettings
  nonMatching: NonMatchingSettings
  updateOwner: UpdateOwnerStrategy
}

export type CrmContactSettingsPayload = {
  createMember?: CreateMemberSettings
  matching?: Partial<MatchingSettings>
  nonMatching?: NonMatchingSettings
  updateOwner?: UpdateOwnerStrategy
}

export type CrmContactTemplateSettings = {
  id: string
  name: string
  settings: CrmContactSettings
}

export enum ProspectPreparationStrategy {
  ContactsAndLeads = 'ContactsAndLeads',
  OnlyLeads = 'OnlyLeads',
}

export type CrmContactWorkspaceSettings = {
  isPerWorkspace: boolean
  prospectPreparationStrategy: ProspectPreparationStrategy
  settings: CrmContactSettings
  templates: CrmContactTemplateSettings[]
}

export type CrmContactWorkspaceSettingsPayload = {
  isPerWorkspace: boolean
  prospectPreparationStrategy: ProspectPreparationStrategy
  settings: CrmContactSettingsPayload
}

export type CrmContactTemplateSettingsPayload = {
  id: string
  settings: CrmContactSettingsPayload
}

export interface CreateOnboardingAccountPayload {
  logo: string
  name: string
}

export interface CreateSingleUseLinkPayload {
  assigneeName?: string
  groupTemplateId?: string
  templateId?: string
  type: SingleUseLinkType
}

export interface UserLicence {
  status: 'Active' | 'Inactive'
}

export enum CalendarType {
  Google = 'Google',
  Microsoft = 'Microsoft',
}

export enum CrmLabel {
  Hubspot = 'HubSpot',
  Salesforce = 'Salesforce',
}

export type CrmType = 'Hubspot' | 'Salesforce'

export type UserLicences = {
  concierge?: UserLicence
  distro?: UserLicence
  events: UserLicence
  meetings: UserLicence
  mildMeetings: UserLicence
}

export interface MeetingsActiveUser {
  calendarStatus: ConnectionStatus
  calendarSync: boolean
  calendarType: CalendarType
  crmStatus: ConnectionStatus
  crmType: CrmType
  defaultWorkspace: string
  details: {
    conference?: string
    isPhoneVerified?: boolean
    phone?: string
    title?: string
  }
  email: string
  gongErrorMessage?: string
  gongStatus: ConnectionStatus
  googleStatus: ConnectionStatus
  gotoMeetingStatus: ConnectionStatus
  hasExtension: boolean
  hasVideo: boolean
  hopinStatus: ConnectionStatus
  hubspotStatus: ConnectionStatus
  id: string
  isAdmin: boolean
  licences: UserLicences
  microsoftStatus: ConnectionStatus
  name: string
  phoneVerified: boolean
  queues?: Queue[]
  ringCentralStatus: ConnectionStatus
  salesforceStatus: ConnectionStatus
  webexStatus: ConnectionStatus
  workspaces: Workspaces
  zoomStatus: ConnectionStatus
}

export type MeetingsActiveUserBrief = Pick<
  MeetingsActiveUser,
  'id' | 'isAdmin' | 'licences' | 'workspaces'
>

export interface DistroTeam {
  id: string
  name: string
  wokspaceId: string
}

export interface UserTableUpdateEntry {
  logs: {
    distroTeams?: Array<DistroTeam>
  }
  table: MeetingsActiveUser
}

export interface UpdateTableV2Payload {
  dry: boolean
  usersChanged: Partial<MeetingsActiveUser>[]
}

export interface LicensesTableRequest {
  filters?: {
    defaultWorkspace?: string
    licenseType?: string
    search?: string
  }
  page: number
  pageSize: number
  sort?: ScrollableTableSortType[]
}

export interface LicensesTableResponse extends ScrollableTableResponse<MeetingsActiveUser> {
  pendingUsersCount: number
}

export interface UserSettings extends WorkingInterval {
  addCrm: boolean
  crmSettings?: {
    inviteInternalUsers: InviteInternalUsersStrategy
  }
  favouriteTemplates: string[]
  guestsCanModifyEvent: boolean
  hoursPerDay: HourPerDayRecord
  id: string
  includeWeekends: boolean
  online: boolean
  onlineSelector: OnlineSelector
  roles: { role: string; workspaceId: string }[]
  template: string
}

export interface OnboardingSearchUser {
  email: string
  existing?: {
    id: string
    licences: MeetingsActiveUser['licences']
    workspaces: any[]
  }
  invite?: InviteToMeetings
  name?: string
  salesforce?: { salesforceId: string; title?: string }
  status: 'Invited' | 'Existing'
}

export interface SFUser {
  active: boolean
  city?: string
  companyName?: string
  country?: string
  email: string
  firstName?: string
  id: string
  lastName?: string
  liveops?: string
  name?: string
  profileId?: string
  smallPhotoUrl?: string
  timeZone?: string
  title?: string
  userRoleId?: string
  userType?: string
  username?: string
}

export interface ScrollableTableResponse<T> {
  page: number
  pageSize: number
  result?: T[]
  results: T[]
  sort?: ScrollableTableSortType[]
  total: number
}

export interface ScrollableTableSortType {
  ascending: boolean
  name: string
}

export interface ScrollableTableRequest {
  filters?: {
    [key: string]: any
    search?: string
  }
  page: number
  pageSize: number
  sort?: ScrollableTableSortType[]
}

export enum ScrollableTableRequestV2FilterTypes {
  AssigneeFilter = 'AssigneeFilter',
  AttendanceFilter = 'AttendanceFilter',
  BookedByFilter = 'BookedByFilter',
  CalendarFilter = 'CalendarFilter',
  EndFilter = 'EndFilter',
  MeetingTypeFilter = 'MeetingTypeFilter',
  RoomFilter = 'RoomFilter',
  StartFilter = 'StartFilter',
  UtmReportFilter = 'UtmReportFilter',
}

export interface ScrollableTableRequestV2Filter {
  type: ScrollableTableRequestV2FilterTypes
  value: any
}

export interface ScrollableTableRequestV2 {
  filters?: ScrollableTableRequestV2Filter[]
  page: number
  pageSize: number
  query: string
  size: number
}

export type LastGeneratedTemplate = {
  createdBy?: {
    email: string
    id: string
    name: string
  }
  lastGenerated?: number
  template: Template
}

export interface ProfileDetailsResponse {
  displayName: string
  email: string
  etag: string
  firstName?: string
  id: string
  image: string
  lastName?: string
  lastUpdate: string
  withoutJwt: string
}

export interface PersonalPageStyles {
  backgroundColor: string
  primaryColor: string
}

export interface PersonalPageOnlineProfiles {
  email: string
  facebook: string
  linkedin: string
  twitter: string
}

export interface PersonalPageMeetingTypes {
  description?: string
  duration?: number
  id: string
  linkName?: string
  name: string
  userId?: string
  workspaceId?: string
  workspaceName?: string
}

export interface CompanyCustomMeta {
  createdBy: string
  createdOn: string
}

export interface CompanyClearbitMeta {
  clearbitTrackingId: string
  id: string
}

export interface PersonalPageCompanyMeta {
  clearbitMeta?: CompanyClearbitMeta
  customMeta?: CompanyCustomMeta
}

export interface PersonalPageCompany {
  brief?: string
  imageUrl?: string
  meta?: PersonalPageCompanyMeta
  name: string
}

export interface PersonalPageTestimonialPost {
  companyBrief?: string
  companyImageUrl?: string
  companyName?: string
  contactEmail?: string
  contactImageUrl?: string
  contactName?: string
  contactTitle?: string
  testimonialQuote?: string
}

export interface TestimonialContact {
  company: PersonalPageCompany
  email?: string
  imageUrl?: string
  meta?: PersonalPageCompanyMeta
  name?: string
  title?: string
}

export interface PersonalPageTestimonialGet {
  contact: TestimonialContact
  quote: string
}

export interface WorkspacePreferences {
  allowBusy: boolean
  allowReassign: boolean
  allowReserve: boolean
  crmContact: CrmContactWorkspaceSettingsPayload
  crmEvent: CrmEventWorkspaceSettingsPayload
  deleteInSalesforce: false
  enabledLanguages: string[]
  endlessRoundRobin: boolean
  excludeCancelled: boolean
  excludeNoShow: boolean
  hideReport: boolean
  id: string
  matchContact: boolean
  matchContactWebsite: boolean
  multiStrategy: MultiStrategy
  roundRobinStart: number
  showAllTitles: boolean
  showBranding: boolean
  showNext: boolean
  skipVacations: boolean
  span: number
  utmCrmSettings?: {
    salesforce: {
      contact: Partial<Record<keyof UTMParameters, string>>
      lead: Partial<Record<keyof UTMParameters, string>>
    }
  }
  workspaceId: string
  workspaceVisibility?: 'HideFromAll' | 'ShowToAll' | 'PerUser'
}

export interface WorkspaceVisibility {
  visibility: { [userId: string]: boolean }
}

export interface AccountSearchResponse {
  accountHealthScore: number
  activeUsers: number
  conciergeCallsConnected: number
  conciergeCallsInitiated: number
  conciergeMeetingsBooked: number
  freeUserLicences: number
  id: string
  licenses: number
  name: string
  nrOfEmployees: number
  productLicences: any
  totalMeetingsBooked: number
}

export interface CreateCrmEntityBase {
  email: string
  firstName: string
  lastName: string
}

export interface CreateLeadPost extends CreateCrmEntityBase {
  company: string
}

export interface CreateContactPost extends CreateCrmEntityBase {
  accountId: string
}

export interface SmsReplyHandler {
  active: boolean
  // text sent back to user
  order?: number // value
  text: string
  value: string
}

export interface Reminder {
  active: boolean
  body: string
  default: boolean
  id: string
  kind?: 'Email' | 'Sms'
  name: string
  rules?: {
    accepted: boolean
    boundary: { active: boolean; direction: number; value: number }
    days: { active: boolean; values: string[] }
    declined: boolean
    difference: { active: boolean; multiplier: number; value: number }
    markCanceled?: SmsReplyHandler
    markConfirmed?: SmsReplyHandler
    markSendText?: SmsReplyHandler
    sendFromLocalAreaNumber: boolean
  }
  sendFrom: 'Booker' | 'Assignee'
  sendTo: 'AllGuests' | 'MainGuest'
  templates: string[]
  title: string
  trigger: {
    at: { active: boolean; hour: number; offset: number }
    before: { active: boolean; multiplier: number; value: number }
  }
  userId?: string
  workspaceId?: string
}

export interface ReminderTable {
  id: string
  lastModified?: number
  lastModifiedBy?: {
    displayName: string
    email: string
    id: string
  }
  name: string
  templates: string[]
  type?: string
  workspaceId?: string
}

export interface ReminderPayload extends Omit<Reminder, 'id'> {
  id?: string
}

export interface ReminderStatus {
  errors: string[]
  name: string
  reminderId: string
  run: number
  setToSkip: boolean
  status?: 'Done' | 'Scheduled' | 'RemindInProgress'
}

export interface DistroUserData {
  displayName: string
  id: string
}

export interface RouterPreviewUser extends DistroUserData {
  email: string
}

export type PublishedDraftStructure<Published, Draft = Published> = {
  id: string
} & (
  | {
      draft: Draft
      published: Published
    }
  | { draft: Draft }
  | { published: Published }
)

export interface RulesetData {
  formula?: string
  id: string
  lastModifiedAt: string
  lastModifiedBy?: DistroUserData
  name: string
  rules: Rule[]
}

export type Ruleset = PublishedDraftStructure<RulesetData>

export type DistroEntitiesUsages = {
  crmRouterDrafts?: CrmRouterData[]
  crmRouters?: CrmRouterData[]
}

export interface TeamMember {
  id: string
  name: string
  weight: number
}

export interface TeamMemberV2 {
  addedAt?: string
  id: string
}

export interface Team {
  created: string
  createdBy: DistroUserData
  id: string
  members: TeamMember[]
  name: string
  updated: string
  updatedBy: DistroUserData
  workspaceId: string
}

export interface TeamV2 {
  createdBy?: DistroUserData
  id: string
  members: TeamMemberV2[]
  name: string
  updated: string
  workspaceId: string
}

export interface TeamUpdateRequest {
  members: TeamMember[]
  name: string
  workspaceId: string
}

export type TeamWithPublishState = Team & {
  hasBeenPublished: boolean
  status: 'draft' | 'published'
}

export type TeamResponse = PublishedDraftStructure<Team>

export type TeamsUserInstallationStatus = 'Installed' | 'Uninstalled'

export interface TeamsUserProfile {
  connections?: {
    salesforce: {
      id: string
      profileId: string
    }
  }
  displayName: string
  email: string
  id: string
  installationStatus?: TeamsUserInstallationStatus
  isAdmin?: boolean
  licences?: {
    concierge?: UserLicence
    events: UserLicence
    meetings: UserLicence
    mildMeetings: UserLicence
  }
  linkName?: string
  name: string
  timeZone?: string
  usePreferredTimezone?: boolean
  workspaces: Workspaces
}

export interface OtherSalesforce {
  profileId: string
}

export interface UserProfile {
  id: string
  name: string
}

export interface TeamsDbUser {
  connections?: {
    google?: {
      status?: string
    }
    microsoft?: {
      status?: string
    }
    otherSalesforce?: OtherSalesforce[]
    salesforce?: {
      id: string
      profileId?: string
    }
  }
  displayName: string
  email: string
  id: string
  installationStatus: string
  lastLoggedIn?: string
  licences: {
    concierge: UserLicence
    distro: UserLicence
    events: UserLicence
    meetings: UserLicence
    mildMeetings: UserLicence
  }
  timeZone?: string
  workspaces?: Required<Workspaces>
}

export type OwnershipAllowedValues = 'Id' | 'Email'

export interface CrmOwnership {
  field?: string
  object?: CrmEntity
  value?: OwnershipAllowedValues
}

export interface CrmRouterDelay {
  length?: string
  unit?: string
}

export type CrmRouterType = 'NewRecord' | 'AnyFieldValue' | 'SpecificFieldValue'

export interface CrmRouterTrigger {
  delay?: CrmRouterDelay
  field?: string
  object: CrmEntity
  operator?: CrmOperator
  rulesetId?: string
  type: CrmRouterType
  value?: string
}

export type CrmRouterSla = {
  rulesetId?: string
  slas: Sla[]
}

export interface CrmRouterData {
  active: boolean
  assignmentRulesets?: AssignmentRuleset[]
  created: ChangeDetails
  id: string
  linkName: string
  name: string
  serviceLevelAgreements?: CrmRouterSla
  trigger?: CrmRouterTrigger
  updated: ChangeDetails
  workspaceId: string
}

export type CrmRouterRequest = Pick<CrmRouterData, 'name' | 'trigger'> & {
  active?: boolean
  assignmentRulesets: string[]
  id: string
  /** The key is an id of the connected ruleAndTeam */
  ownership?: Record<string, CrmOwnership>
  serviceLevelAgreements?: CrmRouterSla
}

export type DraftCrmRouter = Omit<CrmRouterData, 'active'> & { active: never }
export type CrmRouter = PublishedDraftStructure<CrmRouterData, DraftCrmRouter>

export interface DistroMetadata {
  createdAt: string
  createdBy: string // string containing type and id: "user/123213213"
  revision: number
  updatedAt: string
  updatedBy: string
}

export type CrmFieldsResponse = Record<
  CrmEntity,
  {
    label: string
    picklistValues: PickListValue[]
    rawValue: string
    value: string
  }[]
>

export type CrmCustomEntity = string
export type CrmCustomTag = string

export type RulesetRequest = {
  formula?: string
  name: string
  rules: Rule[]
}
export type DuplicateRulesetRequest = { name?: RulesetData['name'] }
export type DraftRuleset = RulesetData
export type PublishedRuleset = RulesetData & Omit<Ruleset, 'draft' | 'published'>

export interface Redirect {
  closeLink?: string
  closeStrategy?: string
  disqualifiedLink?: string
  enabled: boolean
  link?: string
  phoneCallEndLink?: string
  phoneCallRedirectEnabled: boolean
  redirectCallTimeout: number
  redirectCallTimeoutStrategy: string
  timeout: number
}

export interface InboundRouter {
  active: boolean
  defaultBooker: string
  form: string
  id: string
  linkName: string
  name: string
  queues: {
    id: string
    order: number
  }[]
  redirect?: Redirect
}

export interface InboundRouterByLink extends InboundRouter {
  workspaceId: string
}

export interface Campaign {
  id: string
  name: string
}

export interface CampaignStatus {
  active: boolean
  label: string
  value: string
}

export interface PlaceholderCalendar {
  calendarId: string
  capacity: number
  eventId: string
  id: string
  isActive: boolean
  name: string
  owner: {
    email: string
    id: string
    name: string
  }
}

export type CrmSyncStatus = 'NonExistent' | 'InProgress' | 'Finished' | 'Failed'

export interface AssignmentRulesetRulesAndTeams {
  id?: string
  ownership?: CrmOwnership
  rulesetId?: string
  teamId?: string
}

export interface ChangeDetails {
  timestamp: string
  user: DistroUserData
}

export interface AssignmentRulesetCreateBody {
  name?: string
  rulesAndTeams?: AssignmentRulesetRulesAndTeams[]
  workspaceId: string
}

export interface AssignmentRulesetUpdateBody {
  id: string
  name: string
  rulesAndTeams: AssignmentRulesetRulesAndTeams[]
}

export interface AssignmentRuleset extends AssignmentRulesetUpdateBody {
  created: ChangeDetails
  updated?: ChangeDetails
  workspaceId: string
}

export interface BookerRouteSettings {
  readonly phoneActionNoReps: 'ScheduleMeetingForLater' | 'KeepTryingReps' | 'DoNothing'
  readonly phoneActionProspectQualified: 'AutomaticCall' | 'ProspectChooseMeetingOrCall'
  readonly phoneUri?: string
  readonly redirect?: Readonly<{
    closeLink?: string
    closeStrategy: 'Landing' | 'Stay' | 'Uri'
    disqualifiedLink?: string
    enabled: boolean
    link?: string
    phoneCallEndLink?: string
    phoneCallRedirectEnabled: boolean
    redirectCallTimeout: number
    redirectCallTimeoutStrategy: string
    timeout: number
  }>
  readonly routeId: string
  /**
   * RouterId is optional, as prior to some migration back in 2019 inbound-routers didn't have ids.
   * Migration to the new model (with ids) was not fully done, so there are still some old
   * inbound-routers without ids. This is a technical debt on BE side, but we need to be prepared to
   * get route without routerId for now. When routerId is not provided we should assume that there
   * is no router (as we cannot reach it anyway).
   */
  readonly routerId?: string
  readonly routerImage?: string
  /**
   * RouterName is optional, as prior to some migration back in 2019 inbound-routers didn't have
   * ids. There is no option to link those old routers with routes created based on them. In such
   * cases there won't be routerName here. When routerName is not provided we should assume that
   * there is no router (as we cannot reach it anyway).
   *
   * @see {@link BookerRouteSettings.routerId} for more info
   */
  readonly routerName?: string
  readonly uiSettings: Readonly<{
    visualStyle: Readonly<{ buttonColor: string; font: string; fontColor: string }>
    wordingStyle: {
      readonly [language: string]: Readonly<{
        bookingStyle: Readonly<{
          body: string
          buttonLabel: string
          success: string
        }>
        common: Readonly<{
          body: string
          bookingButtonLabel: string
          callButtonLabel: string
          congratulation?: string
          videoButtonLabel: string
          welcome?: string
        }>
        phoneStyle: BookerRouterMediaTypeStyle
        videoStyle: BookerRouterMediaTypeStyle
      }>
    }
  }>
  readonly uri?: string
  readonly videoUri?: string
}

interface BookerRouterMediaTypeStyle {
  readonly body: string
  readonly bookLabel: string
  readonly buttonLabel: string
  readonly fault: string
  readonly success: string
  readonly triggering: string
}

export interface CrmRouterLogInfo {
  active: boolean
  crmRouterName: string
  id: string // same as CRM Router id
  recordsTimedDelayed: number
  recordsTimedError: number
  recordsTimedNoMatch: number
  recordsTimedNoMatchTrigger: number
  recordsTimedSuccess: number
}

export interface CrmRouterLogs {
  crmRouters: CrmRouterLogInfo[]
}

export type LogRecordStatus =
  | 'Routed'
  | 'Error'
  | 'NoMatch'
  | 'Delayed'
  | 'RoutedSLA'
  | 'DiscardedByDelay'
  | 'NoMatchTrigger'

export interface DistroLogDetailsRequestPayload {
  dateFrom: string
  dateTo: string
  page?: number
  pageSize?: number
  query?: string
  status?: LogRecordStatus[]
  userIds?: string[]
}

export interface CrmRouterPersisted extends Omit<CrmRouter, 'trigger' | 'assignmentRulesets'> {
  assignmentRulesets: AssignmentRulesetMatched[]
  trigger: CrmRouterTriggerLogs
}

export interface AssignmentRulesetMatched
  extends Omit<AssignmentRulesetUpdateBody, 'rulesAndTeams'> {
  isSuccessful: boolean
  routes: AssignmentRulesetRulesAndTeamsPersisted[]
}

export interface AssignmentRulesetRulesAndTeamsPersisted
  extends Omit<AssignmentRulesetRulesAndTeams, 'teamId' | 'rulesetId'> {
  rule?: RulesetDataMatched
  team?: Team
}

export interface RulesetDataMatched extends Omit<RulesetData, 'rules'> {
  isSuccessful: boolean
  results: RuleMatched[]
}

export interface RuleMatched extends Rule {
  data: string
  error?: string
  isSuccessful: boolean
}

export interface CrmRouterTriggerLogs extends Omit<CrmRouterTrigger, 'rulesetId'> {
  isSuccessful: boolean
}

export interface CrmRouterTriggerWithDelay extends Omit<CrmRouterTrigger, 'rulesetId' & 'delay'> {
  delay: CrmRouterDelay
  isSuccessful: boolean
}

export type SlaStatus = 'Saved' | 'NotMatched' | 'Error' | 'ActionDone' | 'Matched'

export interface SlaLog extends Sla {
  additionalData: { previous_assignee?: DistroUserData }
  id: string
  rulesetId: string
  status: SlaStatus
  timestamp?: string
}

export interface EventUpdate {
  dateTime: string
  fieldName: string
  from: string
  to: string
}

export type StatusesWithTrigger = 'DiscardedByDelay' | 'Delayed'

export type LogRecord = {
  assignedTo?: DistroUserData
  assignmentRulesets?: AssignmentRulesetMatched[]
  createdAt: string
  crmId: string
  crmLink: string
  crmObject: { [key: string]: string }
  entity: CrmEntity
  error?: string // this is an error code
  events: Array<EventUpdate>
  id: string
  isDelay: boolean
  label: string
  routerId: string
  routerName: string
  ruleEvaluation?: {
    formula?: string
    id: string
    isSuccessful: boolean
    name: string
    results: RuleMatched[]
  }
  slas?: SlaLog[]
  updatedAt: string
} & (
  | {
      status: StatusesWithTrigger
      trigger: CrmRouterTriggerWithDelay
    }
  | {
      status: Exclude<LogRecordStatus, StatusesWithTrigger>
      trigger?: CrmRouterTriggerLogs
    }
)

export type CrmRouterLogResponse = ScrollableTableResponse<LogRecord>

export enum ActionTypes {
  ReassignRecord = 'ReassignRecord',
  SendEmailReminderToAssignee = 'SendEmailReminderToAssignee',
  SendSlackToAssignee = 'SendSlackToAssignee',
  UpdateFieldOnRecord = 'UpdateFieldOnRecord',
}

type DetailedActionTypes = ActionTypes.UpdateFieldOnRecord

export type SlaUpdateFieldOnRecordAction = {
  field: string
  object: CrmEntity & string
  type: DetailedActionTypes
  value: string
}

export type SlaAction =
  | {
      type: Exclude<ActionTypes, DetailedActionTypes>
    }
  | SlaUpdateFieldOnRecordAction

export type Sla = {
  action: SlaAction
  name: string
  time: {
    length: number
    unit: string
  }
}

export type SlaUpdateRequest = Sla

export interface OrganizationDetails {
  about: string
  logo: string
  name: string
  website: string
}
